/**
 * Shapes returned by the v2 student/parent business validations.
 * POST v2/admin/schools/{school}/students -> 201, or 409 with a `conflict`.
 *
 * Only one conflict is reported per request, so resolving one can reveal the
 * next: the caller must loop until it gets a 201.
 */

export type ConflictSource = 'existing' | 'submitted' | 'acknowledge' | 'remove';

/**
 * The only fields the endpoint will accept a resolution for.
 * `student_parent_limit` takes `remove` + a `user_id`, and only from a client
 * that sends `supports_parent_unlink=1` — without it the server keeps S1 terminal.
 */
export type ResolvableField =
  | 'parent_national_id'
  | 'parent_phone'
  | 'parent_email'
  | 'student_in_other_school'
  | 'student_parent_limit';

/**
 * Terminal fields: reported, never resolved.
 * `parent_already_linked` is raised by the add-parent endpoint only — the
 * add-student one cannot reach it, since it creates the student too.
 */
export type ConflictField = ResolvableField | 'parent_already_linked';

export interface ConflictGuardian {
  user_id?: number;
  parent_name?: string;
  parent_national_id?: string;
  parent_country_code?: string;
  parent_phone?: string;
  parent_phone_number?: string;
  parent_email?: string;
  /**
   * student_parent_limit only. true = the guardian holds a parent role in this
   * school and comes in full; false = another school's guardian, masked by the
   * API to the name and the two `_last4` fields below — nothing more exists.
   */
  in_school?: boolean;
  /** In-school guardians: their other children here, this student excluded. */
  children_in_school?: { id: number; name: string }[];
  parent_national_id_last4?: string;
  parent_phone_last4?: string;
}

/** student_in_other_school reports schools instead of guardians. */
export interface ConflictSchool {
  id: number;
  name: string;
}

export interface BusinessConflict {
  field: ConflictField;
  issue_type: string;
  message: string;
  /** false = terminal: there is no choice that clears it. */
  resolvable: boolean;
  /** Drives the whole panel. Empty means blocked. */
  allowed_sources: ConflictSource[];
  existing_value: ConflictGuardian | ConflictSchool | null;
  submitted_value: ConflictGuardian | ConflictSchool | null;
  /** student_parent_limit only: the two guardians already linked. */
  parents?: ConflictGuardian[];
}

export interface ConflictResponse {
  status: 'conflict' | 'success';
  message?: string;
  conflict?: BusinessConflict;
}

/**
 * Sent back as resolutions[field][source] — never the winning value itself.
 * `remove` also names the guardian to unlink, as resolutions[field][user_id].
 */
export type Resolutions = { [field: string]: { source: ConflictSource; user_id?: number } };

/** What a panel emits when the admin answers a conflict. */
export interface ConflictChoice {
  field: string;
  source: ConflictSource;
  user_id?: number;
}

/** Which guardian key the conflicting field actually refers to. */
export const CONFLICT_VALUE_KEY: { [field: string]: string } = {
  parent_national_id: 'parent_national_id',
  parent_phone: 'parent_phone_number',
  parent_email: 'parent_email',
};

/** Which form control to anchor the panel under, per flow. */
export const CONFLICT_ANCHOR: { [field: string]: string } = {
  parent_national_id: 'parent_national_id',
  parent_phone: 'parent_phone',
  parent_email: 'parent_email',
  student_parent_limit: 'parent_national_id',
  parent_already_linked: 'parent_national_id',
  student_in_other_school: 'student_national_id',
};

export function isSchoolConflict(c: BusinessConflict): boolean {
  return c?.field === 'student_in_other_school';
}

/** S1 the admin can answer by unlinking one of the two guardians. */
export function isParentUnlink(c: BusinessConflict): boolean {
  return c?.field === 'student_parent_limit' && !!c.allowed_sources?.includes('remove');
}

export function isTerminal(c: BusinessConflict): boolean {
  return !!c && (c.resolvable === false || !c.allowed_sources?.length);
}
